import React, { useMemo } from "react";
import styled from "@emotion/styled";

import StatTypes from "types/StatTypes";

import ParkkiTheme from "themes/ParkkiTheme";

import CommonUtils from "services/common/utils";

import InfosTooltip from "components/InfosTooltip";
import StatsContainer from "components/StatsContainer";

const ArrowImage = styled.img`
  width: 10px;
  height: auto;
  margin-right: 8px;
`;

const Label = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  height: 35px;
  font-size: 10px;
  width: 80%;
  text-align: center;
`;

const Normal = styled.div`
  display: flex;
  align-items: baseline;
  text-align: center;
  font-size: 16px;
  margin: 4px;
  color: ${(props: { color: string }) => props.color};
`;

const NormalUnity = styled.label`
  font-size: 12px;
`;

const SubLabel = styled.div`
  font-size: 8px;
`;

const ValueUnity = styled.label`
  font-size: 11px;
  margin-left: 3px;
`;

const Value = styled.div`
  font-size: 21px;
  font-weight: bold;
  margin-bottom: 4px;
`;

const LabelCapitalize = styled.span`
  text-transform: uppercase;
`;

type Props = { data: StatTypes; numberOfSection: number };

function NormalStats({ data, numberOfSection }: Props): JSX.Element {
  const value = useMemo(() => {
    return data?.value?.getValue
      ? data.value.getValue(data?.value)
      : data?.value?.value;
  }, [data?.value]);

  const formattedValue = useMemo(() => {
    return data?.value?.formatValue && value !== null && value !== undefined
      ? data.value.formatValue(value)
      : value;
  }, [data?.value, value]);

  const avg = useMemo(() => {
    return data?.value?.getAvg
      ? data?.value?.getAvg(data?.value)
      : data?.value?.avg;
  }, [data?.value]);

  const Symbol = useMemo(() => {
    const symbol = data?.type?.symbol;
    const way = data?.type?.way ? data?.type?.way(value, avg) : "";
    const color = data?.type.retrieveColor
      ? data?.type.retrieveColor(value, avg)
      : "";

    if (symbol === "arrow") {
      switch (way) {
        case 1:
          return (
            <ArrowImage
              alt={`arrow-up-${color}`}
              src={require(`images/stats-row-arrow-up-${color}.png`)}
            />
          );
        case -1:
          return (
            <ArrowImage
              alt={`arrow-down-${color}`}
              src={require(`images/stats-row-arrow-down-${color}.png`)}
            />
          );
        default:
          return "=";
      }
    } else if (symbol === "sign") {
      switch (way) {
        case 1:
          return "+";
        case -1:
          return "-";
        default:
          return "=";
      }
    } else {
      return "";
    }
  }, [avg, data?.type, value]);

  const normalValue = useMemo(() => {
    return data?.type?.retrieveNormal
      ? data?.type?.retrieveNormal(value, avg)
      : data?.value;
  }, [data?.value, data?.type, avg, value]);

  const normalColor = useMemo(() => {
    switch (
      data?.type?.retrieveColor ? data?.type?.retrieveColor(value, avg) : ""
    ) {
      case "green":
        return ParkkiTheme.colors.green;
      case "red":
        return ParkkiTheme.colors.red;
      case "orange":
        return ParkkiTheme.colors.orange;
      default:
        return "black";
    }
  }, [avg, value, data?.type]);

  if (!data?.toggle) return <></>;

  return (
    <StatsContainer
      disabled={formattedValue === null || formattedValue === undefined}
      headerColor={data?.headerColor}
      order={data?.type?.order}
      numberOfSection={numberOfSection}
    >
      <Value>
        {formattedValue !== null && formattedValue !== undefined
          ? CommonUtils.formatNumber(formattedValue)
          : "-"}
        <ValueUnity>{data?.type.valueUnity}</ValueUnity>
      </Value>
      <Label>
        <LabelCapitalize>{data?.label || ""}</LabelCapitalize>
        {data?.labelInfos ? <InfosTooltip infos={data?.labelInfos} /> : null}
      </Label>
      {data?.type?.toggle &&
      normalValue !== null &&
      normalValue !== undefined ? (
        <Normal color={normalColor}>
          {Symbol}{" "}
          {data?.value?.formatNormal
            ? CommonUtils.formatNumber(data?.value?.formatNormal(normalValue))
            : CommonUtils.formatNumber(normalValue)}
          <NormalUnity> {data?.type?.unity}</NormalUnity>
          {data?.infos ? <InfosTooltip infos={data?.infos} /> : null}
        </Normal>
      ) : null}

      <SubLabel>{data?.subLabel || ""}</SubLabel>
    </StatsContainer>
  );
}

export default NormalStats;
